import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';

import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import {ErrorAlert} from '../common/ErrorAlert';
import ButtonBar from '../common/ButtonBar';
import {ErrorResponse, InventorySale, SaveInventorySaleRequest} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import * as paths from '../paths';
import {IdPageUrlParams} from '../paths';
import SaleForm from './SaleForm';
import {Alert, Button} from 'antd';
import locales from '../locales';
import {
  showDeleteConfirm,
  showDeleteSuccessNotification,
  showErrorModal,
  showSaveConfirm,
  showSaveSuccessNotification,
} from '../utils/dialogs';
import {showPrintPopup} from "../print/PrintPopup";
import PrintInventorySalePage from "./PrintInventorySalePage";
import {InvoiceSource, InvoiceType} from "../invoicing/invoice";
import {InvoiceEntry} from "../api/dtos/summaries";
import {AmountType} from "../common/AmountType";
import * as dateUtils from "../utils/date-utils";
import {formItemLayout} from "../utils/form-utils";
import FormItem from "antd/lib/form/FormItem";
import InvoiceCell from "../invoicing/InvoiceCell";

interface State {
  editable: boolean;
  sale?: InventorySale;
  error?: ErrorResponse;
}

class SaleDetailsPage extends React.Component<RouteComponentProps<IdPageUrlParams>, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    editable: false,
  };

  public componentDidMount() {
    this.loadSale();
  }

  public componentDidUpdate(prevProps: Readonly<RouteComponentProps<IdPageUrlParams>>) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.loadSale();
    }
  }

  public render(): React.ReactNode {
    const {sale, error, editable} = this.state;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 24}}>
          {!sale?.deletedAt && (!sale?.invoice || sale?.invoice?.type === InvoiceType.STORNO) && (
            <Button type="primary" icon="file-text"
                    onClick={this.onCreateInvoice}
            >
              {locales.invoice_create}
            </Button>
          )}
          {sale && (
            <Button icon="printer" onClick={this.onPrintClick}>
              {locales.print}
            </Button>
          )}
          {sale && !sale.deletedAt &&
              <Button
                  type="danger"
                  icon="delete"
                  onClick={this.onDelete}
                  disabled={!sale || editable || error !== undefined || !!(sale.invoice && sale.invoice.type !== InvoiceType.STORNO)}
              >{locales.delete}</Button>
          }
          {sale && !sale.deletedAt &&
              <Button
                  type="primary"
                  icon="edit"
                  onClick={this.onEditClick}
                  disabled={!sale || editable || error !== undefined || !!(sale.invoice && sale.invoice.type !== InvoiceType.STORNO)}
              >{locales.edit}</Button>
          }
        </ButtonBar>

        {sale && (
          <>
            {!sale.deletedAt && sale.invoice && sale.invoice.type !== InvoiceType.STORNO && (
              <Alert
                style={{marginBottom: 24}}
                type="info"
                showIcon={true}
                message={locales.sale_edit_delete_requires_no_invoice}
              />
            )}
            {sale.invoice &&
                <FormItem {...formItemLayout} label={locales.invoice}>
                    <InvoiceCell invoice={sale.invoice} finalized={false}/>
                </FormItem>
            }
            <FormItem {...formItemLayout} label={locales.id}>
              <b>{sale.saleNumber}</b>
            </FormItem>
            {sale.deletedAt && (
              <FormItem {...formItemLayout} label={locales.delete_date}>
                {dateUtils.formatDateTimeOrNull(sale.deletedAt) || '-'}
              </FormItem>
            )}
            {!sale.deletedAt && (
              <FormItem {...formItemLayout} label={locales.last_changed_at}>
                {dateUtils.formatDateTimeOrNull(sale.lastChangedAt) || '-'}
              </FormItem>
            )}
            <FormItem {...formItemLayout} label={locales.last_changed_by}>
              {sale.lastChangedBy || '-'}
            </FormItem>

            <SaleForm
              sale={sale}
              editable={editable}
              onFormSubmit={this.onFormSubmit}
              onFormCancel={this.onFormCancel}
              onSaleReload={this.loadSale}
            />
          </>
        )}
        {error && <ErrorAlert style={{marginTop: 16}} error={error}/>}
      </>
    );
  }

  private getIdParam = (): number => Number(this.props.match.params.id);

  private loadSale = () => {
    const promise = furoApi.sales.get(this.getIdParam())
      .then(this.onLoadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadSuccess = (sale: InventorySale) => {
    this.setState({sale, error: undefined});
    setAppHeaderSubtitle(this.props, sale.client?.name);
  };

  private onEditClick = () => {
    this.setState({editable: true, error: undefined});
  };

  private onFormSubmit = (request: SaveInventorySaleRequest) => {
    showSaveConfirm(() => this.saveSale(request));
  };

  private saveSale = (request: SaveInventorySaleRequest) => {
    const promise = furoApi.sales.save(this.getIdParam(), request)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onSaveSuccess = () => {
    showSaveSuccessNotification();
    this.setState({editable: false}, this.loadSale);
  };

  private onFormCancel = () => {
    this.setState({editable: false, error: undefined}, this.loadSale);
  };

  private onDelete = () => {
    showDeleteConfirm(this.deleteSale, locales.sale_delete_confirm);
  };

  private deleteSale = () => {
    const promise = furoApi.sales.delete(this.getIdParam())
      .then(this.onDeleteSuccess)
      .catch((e) => {
        showErrorModal(e.details.message);
      });
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onDeleteSuccess = () => {
    showDeleteSuccessNotification();
    this.props.history.goBack();
  };

  private onPrintClick = () => {
    const {sale} = this.state;
    if (!sale) {
      return;
    }
    showPrintPopup(locales.sales_details, <PrintInventorySalePage sale={sale}/>)
  }

  private onCreateInvoice = () => {
    const {sale} = this.state;
    if (!sale) {
      return;
    }
    const entries: InvoiceEntry[] = sale.entries.map(entry => {
      const price = Number((entry.cost * (1 - (entry.discount ?? 0) / 100)).toFixed(1));

      return {
        name: entry.name,
        amount: entry.amount,
        price,
        amountType: AmountType.PCS,
        comment: entry.discount ? `${locales.discount}: ${entry.discount}%` : null,
        total: price * entry.amount,
      };
    });

    if (sale.transportCost !== undefined && sale.transportCost > 0) {
      entries.push({
        name: locales.transport_cost,
        amount: 1,
        price: sale.transportCost!,
        amountType: AmountType.PCS,
        comment: null,
        total: sale.transportCost,
      });
    }

    this.props.history.push(paths.createInvoicePath, {
      client: sale.client,
      entries,
      source: InvoiceSource.SALE,
      sourceId: sale.id,
      invoiceComment: sale.invoiceComment,
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default withRouter(SaleDetailsPage);



// WEBPACK FOOTER //
// ./src/sales/SaleDetailsPage.tsx